/**
 * One formula, as the builder holds it before it is saved, and the line that
 * says it back in plain words.
 *
 * Everything here is pure. The screen keeps a draft in a store, taps rows to
 * change it, and reads the sentence and the preview out of this file. That is
 * the same division `link-draft.ts` uses, for the same reason: the rule for
 * what a formula means must be testable without a phone.
 *
 * The evaluator itself is NOT here. `evaluateFormula`, `checkFormula` and
 * `formulaSentence` come from `@alluzo/shared`, because Section 5.7 rule 6 says
 * the device runs the same rule as the server, and one implementation is the
 * only way that holds.
 *
 * **A TREE, and a half-built one.** The draft used to be a flat chain of terms
 * because the builder offered no nesting. It offers nesting now, so the draft
 * is the tree itself plus one node the tree does not have: a `gap`, the empty
 * slot a person has opened and not yet filled. A tree with a gap in it cannot
 * be a `FormulaSpec` — `draftToSpec` answers null while one is open — and that
 * is the whole of the difference between a draft and a formula.
 *
 * **The rendering model, in one paragraph.** A `calc` folds LEFT. So the left
 * spine of a chain of calcs is drawn FLAT — piece, operator, piece, operator —
 * exactly as it is read, and a `calc` sitting on the RIGHT of another is drawn
 * as a bracketed block indented under a guide line. That is not a drawing
 * choice: it is the same rule `formulaSentence` follows when it puts brackets
 * on the right and never on the left. One rule, two renderings of it, and no
 * precedence for anybody to learn.
 *
 * **Paths.** A place in the tree is an array of child indices in `childrenOf`
 * order, so `[]` is the root and `[0, 1]` is the second child of the first.
 * Every editor takes one. Nothing holds a node by reference, because a tree
 * that is rebuilt on every edit has no stable references to hold.
 */
import {
  DEFAULT_TAKERS,
  FORMULA_MAX_DEPTH,
  FORMULA_MAX_NODES,
  checkFormula,
  defaultProblems,
  formulaGraphProblems,
  showWhenProblems,
  editableWhenProblems,
  mustBeProblems,
  evaluateFormula,
  formulaFieldsRead,
  formulaLinesRead,
  formulaResultFor,
  formulaSentence,
  isComputedType,
  isNumericType,
  resultAccepts,
  readFormulaSpec,
  readableFieldType,
  workedResultOf,
  testNeedsRight,
  type DateUnit,
  type DefaultOptions,
  type EntryLinesWrite,
  type ValueTypeId,
  type FormulaAggOp,
  type FormulaCalcOp,
  type FormulaNode,
  type FormulaOnMissing,
  type FormulaProblem,
  type FormulaResultType,
  type FormulaSpec,
  type FormulaTestOp,
  type FormulaValues,
  type FormulaLookup,
  type LinesReader,
  type OptionKeyReader,
  type FormulaWordLookup,
  type RecordSchemaField,
  type RecordSchemaResponse,
  formulaOf,
  localDayOf,
} from '@alluzo/shared';

import { t, tData } from '@/lib/i18n';

/* --------------------------------------------------------------- the draft */

/**
 * A node of the tree being built.
 *
 * Every shape `FormulaNode` has, plus one: a `gap`. A gap is a slot the person
 * has opened — "or else …", the right half of a bracket they just opened — and
 * has not yet answered. It exists only in the builder. Nothing may be saved
 * while one is open, so the gap never has to be readable by anything else.
 */
export type DraftNode =
  /** An empty slot waiting for a piece. The only kind that is not a FormulaNode. */
  | { readonly kind: 'gap' }
  | { readonly kind: 'field'; readonly fieldId: string }
  | { readonly kind: 'hop'; readonly via: string; readonly fieldId: string }
  | { readonly kind: 'const'; readonly value: number | string | boolean }
  | { readonly kind: 'calc'; readonly op: FormulaCalcOp; readonly left: DraftNode; readonly right: DraftNode }
  | { readonly kind: 'round'; readonly of: DraftNode; readonly decimals: number }
  | {
      readonly kind: 'if';
      readonly left: DraftNode;
      readonly op: FormulaTestOp;
      readonly right?: DraftNode;
      readonly then: DraftNode;
      readonly otherwise: DraftNode;
    }
  | { readonly kind: 'days_between'; readonly from: DraftNode; readonly to: DraftNode }
  | { readonly kind: 'concat'; readonly parts: readonly DraftNode[] }
  | { readonly kind: 'coalesce'; readonly of: readonly DraftNode[] }
  | { readonly kind: 'agg'; readonly op: FormulaAggOp; readonly lines: string; readonly of?: string }
  | {
      readonly kind: 'date_add';
      readonly date: DraftNode;
      readonly amount: DraftNode;
      readonly unit: DraftNode;
      readonly validTill: boolean;
    }
  | { readonly kind: 'today' };

/** A place in the tree: child indices in `childrenOf` order. `[]` is the root. */
export type DraftPath = readonly number[];

/**
 * What the builder is being used FOR, and what Save does with the answer.
 *
 * `field` adds a formula field to the structure draft, which is what the
 * builder has always done. `link` answers a mapping into the link draft for one
 * field on the link's target, and its `result` is not the person's to choose:
 * it is fixed by the type of the field the answer has to land in, so an amount
 * lands in an amount and a count never does.
 *
 * `insight` answers a row source into the insight draft: the recipe is worked
 * out on every row and then added up, so nothing is stored on the row at all.
 * Its result is not a choice either — a card adds up numbers, and any flavour
 * of number will do, which is exactly what a `number` result accepts.
 */
export type FormulaPurpose =
  | { readonly kind: 'field' }
  | {
      readonly kind: 'link';
      /** The field on the link's TARGET record this formula fills. */
      readonly targetFieldId: string;
      readonly result: FormulaResultType;
    }
  | { readonly kind: 'insight' }
  | {
      readonly kind: 'default';
      /**
       * The field in the structure draft this default belongs to: a real id on
       * a saved field, the draft's own handle on one added in this session, and
       * `PENDING_FIELD_KEY` on one that is still being described.
       */
      readonly fieldKey: string;
      /**
       * Not the person's to choose either, and for the same reason a link's is
       * not: the answer has to land in a field of a type that already exists.
       * `defaultResultFor` is where it comes from.
       */
      readonly result: FormulaResultType;
      /** What the field IS, which is what `defaultProblems` checks the fit against. */
      readonly fieldType: ValueTypeId;
    }
  | {
      /**
       * When a field shows on the form (Decision 11 of
       * docs/plans/entry-lines-and-layout.md): a yes or a no, so the result is
       * fixed at `boolean` and nobody chooses it. `fieldKey` is the field the
       * rule belongs to, as on a default.
       */
      readonly kind: 'show_when';
      readonly fieldKey: string;
      /**
       * Which yes-or-no rule of the field: when it shows (absent, or `show`),
       * or when a person may change it (`edit`, F10 of
       * docs/plans/field-relations.md). Both are read the same way and built
       * in the same builder; only where the answer lands differs.
       */
      readonly rule?: 'show' | 'edit' | 'must';
      /**
       * A Must be rule (`must`, F13): which rule of the list it is, or absent
       * for a new one. A rule on the whole entry carries `RECORD_RULES_KEY`
       * as its `fieldKey`.
       */
      readonly at?: number;
    };

/** The `fieldKey` of a Must be rule on the whole entry rather than on one field (F13). */
export const RECORD_RULES_KEY = '__record_rules__';

/**
 * The key a default carries while its field is still being ADDED.
 *
 * A recipe is written against a field, and the add screen has no field yet —
 * only a name, a type, and a couple of switches. So the builder leaves the
 * finished draft in the store instead of writing it, and the add screen takes
 * it off. Empty, because no key this app writes is empty.
 */
export const PENDING_FIELD_KEY = '';

export type FormulaDraft = {
  /** The field id when an existing formula is open. Null while making a new one. */
  readonly editing: string | null;
  readonly label: string;
  readonly result: FormulaResultType;
  readonly onMissing: FormulaOnMissing;
  /** Null before the first piece. Never empty afterwards. */
  readonly root: DraftNode | null;
  readonly purpose: FormulaPurpose;
};

/** The one node a gap is, written once so nothing spells it two ways. */
export const GAP: DraftNode = { kind: 'gap' };

const FIELD_PURPOSE: FormulaPurpose = { kind: 'field' };

/** What a new recipe works out to, before anybody has said what it is for. */
const blankResultFor = (purpose: FormulaPurpose): FormulaResultType => {
  if (purpose.kind === 'link' || purpose.kind === 'default') return purpose.result;
  if (purpose.kind === 'show_when') return 'boolean';
  // A card adds the answer up, and any numeric tree fits a `number` result —
  // "3 litres times 28" and "3 crates times 12" are both numbers to a total.
  return purpose.kind === 'insight' ? 'number' : 'money';
};

export const blankDraft = (purpose: FormulaPurpose = FIELD_PURPOSE): FormulaDraft => ({
  editing: null,
  label: '',
  result: blankResultFor(purpose),
  onMissing: 'blank',
  root: null,
  purpose,
});

export const withLabel = (draft: FormulaDraft, label: string): FormulaDraft => ({ ...draft, label });

export const withResult = (draft: FormulaDraft, result: FormulaResultType): FormulaDraft => ({
  ...draft,
  result,
});

export const withOnMissing = (draft: FormulaDraft, onMissing: FormulaOnMissing): FormulaDraft => ({
  ...draft,
  onMissing,
});

/**
 * What "When a value is empty" says it does. A blank answer shows a dash on a
 * total, but a Show when rule shows the field only on a yes, so there a blank
 * answer hides the field (`isShown`), and the row says so rather than offer a
 * dash nobody will see.
 */
export const whenEmptyWord = (kind: FormulaPurpose['kind'], onMissing: FormulaOnMissing): string =>
  onMissing === 'zero'
    ? t('formula.when_empty_zero')
    : kind === 'show_when'
      ? t('field.show_when.empty_hides')
      : t('formula.when_empty_blank');

/**
 * What a formula must work out to, to fill a field of this type.
 *
 * It lives in `@alluzo/shared` now, beside the table it is read out of, because
 * `defaultProblems` on the server asks the same question of the same table and
 * two copies of that answer could drift. Re-exported here so the link editor,
 * which has asked this file since before there was a shared one, needs no
 * change to keep asking it.
 */
export { formulaResultFor } from '@alluzo/shared';

/**
 * What a DEFAULT on a field of this type must work out to, or null when the
 * type takes no default at all.
 *
 * `formulaResultFor` answers for every type whose value is a value. A `select`
 * and a `reference` are not in it on purpose — an id is not something
 * arithmetic can produce — and yet both take a default, as one of the field's
 * own choices and as one row of the list it reads. Both are stored as text, so
 * text is what the tree answers, and `defaultProblems` keeps the shape rule
 * that says which trees are honest.
 */
export const defaultResultFor = (fieldType: string): FormulaResultType | null => {
  if (!(DEFAULT_TAKERS as readonly string[]).includes(fieldType)) return null;
  return formulaResultFor(fieldType) ?? 'text';
};

/* ------------------------------------------------------------- the walking */

/**
 * The children of one node, in the order a path counts them.
 *
 * A copy of `childrenOf` in `packages/shared/src/formula.ts`, which is private
 * there, widened to know about a gap. It has to agree with that one: the paths
 * this file hands around are the same indices the checker's `measure` walks, so
 * a screen that counted them differently would draw a different tree from the
 * one being saved. An `if` whose op needs no right side has THREE children, so
 * `then` is index 1 rather than 2 — the same shift the shared file makes.
 */
export const childrenOf = (node: DraftNode): readonly DraftNode[] => {
  if (node.kind === 'calc') return [node.left, node.right];
  if (node.kind === 'round') return [node.of];
  if (node.kind === 'if') {
    return node.right === undefined
      ? [node.left, node.then, node.otherwise]
      : [node.left, node.right, node.then, node.otherwise];
  }
  if (node.kind === 'days_between') return [node.from, node.to];
  if (node.kind === 'concat') return node.parts;
  if (node.kind === 'coalesce') return node.of;
  if (node.kind === 'date_add') return [node.date, node.amount, node.unit];
  return [];
};

/** The same node with its children replaced, position for position. */
const withChildren = (node: DraftNode, kids: readonly DraftNode[]): DraftNode => {
  if (node.kind === 'calc') return { ...node, left: kids[0] ?? GAP, right: kids[1] ?? GAP };
  if (node.kind === 'round') return { ...node, of: kids[0] ?? GAP };
  if (node.kind === 'if') {
    return node.right === undefined
      ? { ...node, left: kids[0] ?? GAP, then: kids[1] ?? GAP, otherwise: kids[2] ?? GAP }
      : { ...node, left: kids[0] ?? GAP, right: kids[1] ?? GAP, then: kids[2] ?? GAP, otherwise: kids[3] ?? GAP };
  }
  if (node.kind === 'days_between') return { ...node, from: kids[0] ?? GAP, to: kids[1] ?? GAP };
  if (node.kind === 'concat') return { ...node, parts: kids };
  if (node.kind === 'coalesce') return { ...node, of: kids };
  if (node.kind === 'date_add') return { ...node, date: kids[0] ?? GAP, amount: kids[1] ?? GAP, unit: kids[2] ?? GAP };
  return node;
};

/** The node at one path, or null when the path names nothing. */
export const nodeAt = (root: DraftNode | null, path: DraftPath): DraftNode | null => {
  let held = root;
  for (const index of path) {
    if (held === null) return null;
    held = childrenOf(held)[index] ?? null;
  }
  return held;
};

/** The tree with one node swapped for another. An unreachable path changes nothing. */
export const replaceAt = (
  root: DraftNode | null,
  path: DraftPath,
  node: DraftNode,
): DraftNode | null => {
  if (path.length === 0) return node;
  if (root === null) return null;
  const [index, ...rest] = path;
  const kids = [...childrenOf(root)];
  const kid = kids[index as number];
  if (kid === undefined) return root;
  const replaced = replaceAt(kid, rest, node);
  if (replaced === null) return root;
  kids[index as number] = replaced;
  return withChildren(root, kids);
};

/**
 * The tree with one node taken out.
 *
 * What "out" means depends on what held it, and every answer here is the one a
 * person would expect from the row they tapped:
 *
 *   the root          nothing is left, and the recipe is empty again
 *   a side of a calc  the calc collapses to the OTHER side. Taking Rate out of
 *                     "Litres × Rate" leaves Litres, not "Litres × …"
 *   a part of a list  `or else` and `join words` drop the part; the last two
 *                     collapse to the one that is left, because a fallback with
 *                     nothing to fall back to is not a fallback
 *   anything else     leaves a gap. There is no honest collapse for the `then`
 *                     of an `if`: the question would still be there.
 */
export const removeAt = (root: DraftNode | null, path: DraftPath): DraftNode | null => {
  if (root === null) return null;
  if (path.length === 0) return null;

  const parentPath = path.slice(0, -1);
  const index = path[path.length - 1] as number;
  const parent = nodeAt(root, parentPath);
  if (parent === null) return root;
  const kids = childrenOf(parent);
  if (kids[index] === undefined) return root;

  if (parent.kind === 'calc') {
    return replaceAt(root, parentPath, (index === 0 ? parent.right : parent.left));
  }

  if (parent.kind === 'coalesce' || parent.kind === 'concat') {
    const left = kids.filter((_, at) => at !== index);
    // One part is not a list any more, so the list gives way to what is left.
    if (left.length === 1) return replaceAt(root, parentPath, left[0] as DraftNode);
    return replaceAt(root, parentPath, withChildren(parent, left));
  }

  return replaceAt(root, path, GAP);
};

/** True while any slot is still empty, which is what stops a draft being saved. */
export const hasGaps = (root: DraftNode | null): boolean => {
  if (root === null) return true;
  if (root.kind === 'gap') return true;
  return childrenOf(root).some(hasGaps);
};

/** How big the tree is, against the two caps the checker refuses on. */
export const measureDraft = (root: DraftNode | null): { nodes: number; depth: number } => {
  if (root === null) return { nodes: 0, depth: 0 };
  const kids = childrenOf(root);
  if (kids.length === 0) return { nodes: 1, depth: 1 };
  let depth = 0;
  let nodes = 1;
  for (const kid of kids) {
    const held = measureDraft(kid);
    depth = Math.max(depth, held.depth);
    nodes += held.nodes;
  }
  return { nodes, depth: depth + 1 };
};

/** The line under the recipe: how much room is left, before it runs out. */
export const depthLine = (root: DraftNode | null): string => {
  const held = measureDraft(root);
  return t('formula.depth', { depth: held.depth, max_depth: FORMULA_MAX_DEPTH, pieces: held.nodes, max_pieces: FORMULA_MAX_NODES });
};

/* -------------------------------------------------------------- the editors */

const withRoot = (draft: FormulaDraft, root: DraftNode | null): FormulaDraft => ({ ...draft, root });

/** One piece answered. With no root yet, the answer IS the root. */
export const setPiece = (draft: FormulaDraft, path: DraftPath, node: DraftNode): FormulaDraft =>
  draft.root === null ? withRoot(draft, node) : withRoot(draft, replaceAt(draft.root, path, node));

export const removePiece = (draft: FormulaDraft, path: DraftPath): FormulaDraft =>
  withRoot(draft, removeAt(draft.root, path));

/**
 * One more piece on the chain at this level, with a gap on the right.
 *
 * `calc(<what is there>, op, gap)`. Applied at the TOP of a chain, the new
 * calc's left is the old chain, so the whole thing still folds left and still
 * draws flat: `a × b` becomes `a × b + …`.
 */
export const appendToChain = (
  draft: FormulaDraft,
  path: DraftPath,
  op: FormulaCalcOp = 'multiply',
): FormulaDraft => {
  const held = nodeAt(draft.root, path);
  if (held === null) return draft;
  return setPiece(draft, path, { kind: 'calc', op, left: held, right: GAP });
};

/**
 * Brackets: work this part out first.
 *
 * The same construction as `appendToChain` and deliberately so — the brackets
 * are not a node, they are WHERE the calc sits. Pointed at one piece inside a
 * chain rather than at the chain's top, the new calc lands on the RIGHT of the
 * one above it, and a calc on the right is exactly what the recipe draws as a
 * bracketed block and what `formulaSentence` puts brackets around. Two rows in
 * the sheet, one rule underneath.
 */
export const openBrackets = (
  draft: FormulaDraft,
  path: DraftPath,
  op: FormulaCalcOp = 'multiply',
): FormulaDraft => appendToChain(draft, path, op);

/** The piece becomes the first of "this, or else that". */
export const wrapInCoalesce = (draft: FormulaDraft, path: DraftPath): FormulaDraft => {
  const held = nodeAt(draft.root, path);
  if (held === null) return draft;
  return setPiece(draft, path, { kind: 'coalesce', of: [held, GAP] });
};

/**
 * The piece becomes the ANSWER of a test that is not asked yet.
 *
 * `if <gap> is filled then <the piece> otherwise <gap>`. The piece a person
 * tapped is what they already believe, so it becomes the `then`; the test and
 * the other answer are the two things they still have to say.
 */
export const wrapInIf = (draft: FormulaDraft, path: DraftPath): FormulaDraft => {
  const held = nodeAt(draft.root, path);
  if (held === null) return draft;
  return setPiece(draft, path, { kind: 'if', left: GAP, op: 'filled', then: held, otherwise: GAP });
};

export const wrapInRound = (draft: FormulaDraft, path: DraftPath, decimals: number): FormulaDraft => {
  const held = nodeAt(draft.root, path);
  if (held === null) return draft;
  if (held.kind === 'round') return setPiece(draft, path, { ...held, decimals });
  return setPiece(draft, path, { kind: 'round', of: held, decimals });
};

/** One more choice on an `or else`, or one more part on a `join words`. */
export const appendToList = (draft: FormulaDraft, path: DraftPath): FormulaDraft => {
  const held = nodeAt(draft.root, path);
  if (held === null) return draft;
  if (held.kind === 'coalesce') return setPiece(draft, path, { kind: 'coalesce', of: [...held.of, GAP] });
  if (held.kind === 'concat') return setPiece(draft, path, { kind: 'concat', parts: [...held.parts, GAP] });
  return draft;
};

/** The four operators, in the order a shop reaches for them. */
export const CALC_OPS: readonly FormulaCalcOp[] = ['multiply', 'divide', 'add', 'subtract'];

/** Tapping the operator between two pieces walks the four. Four is tappable. */
export const cycleOpAt = (draft: FormulaDraft, path: DraftPath): FormulaDraft => {
  const held = nodeAt(draft.root, path);
  if (held === null || held.kind !== 'calc') return draft;
  const next = CALC_OPS[(CALC_OPS.indexOf(held.op) + 1) % CALC_OPS.length] ?? 'multiply';
  return setPiece(draft, path, { ...held, op: next });
};

/** The eight questions an `if` may ask, in the order the sheet lists them. */
export const TEST_OPS: readonly FormulaTestOp[] = [
  'filled',
  'empty',
  'eq',
  'ne',
  'gt',
  'gte',
  'lt',
  'lte',
];

/**
 * The test one `if` asks.
 *
 * An op that asks about one side only drops the right half, and one that needs
 * two opens a gap for it. Leaving a stale right half behind would put a value on
 * screen that the evaluator never looks at.
 */
export const withTestOpAt = (draft: FormulaDraft, path: DraftPath, op: FormulaTestOp): FormulaDraft => {
  const held = nodeAt(draft.root, path);
  if (held === null || held.kind !== 'if') return draft;
  if (!testNeedsRight(op)) {
    return setPiece(draft, path, {
      kind: 'if',
      left: held.left,
      op,
      then: held.then,
      otherwise: held.otherwise,
    });
  }
  return setPiece(draft, path, { ...held, op, right: held.right ?? GAP });
};

/**
 * A date moved on, reshaped where it stands (F4 to F6 of
 * docs/plans/field-relations.md): a constant unit picked from the four, or
 * valid till turned on or off. The date and the amount keep their pieces.
 */
export const withDateAddAt = (
  draft: FormulaDraft,
  path: DraftPath,
  change: { readonly unit?: DateUnit; readonly validTill?: boolean },
): FormulaDraft => {
  const held = nodeAt(draft.root, path);
  if (held === null || held.kind !== 'date_add') return draft;
  return setPiece(draft, path, {
    ...held,
    ...(change.unit === undefined ? {} : { unit: { kind: 'const', value: change.unit } }),
    ...(change.validTill === undefined ? {} : { validTill: change.validTill }),
  });
};

/** A date moved on with nothing in it yet: a month, the plain day. */
export const blankDateAdd = (date: DraftNode = GAP): DraftNode => ({
  kind: 'date_add',
  date,
  amount: GAP,
  unit: { kind: 'const', value: 'months' },
  validTill: false,
});

/* ------------------------------------------------------------ to and from */

/** The tree, once nothing in it is still a gap. Null while one is open. */
export const draftToSpec = (draft: FormulaDraft): FormulaSpec | null => {
  if (draft.root === null || hasGaps(draft.root)) return null;
  return { result: draft.result, onMissing: draft.onMissing, expr: draft.root as FormulaNode };
};

/**
 * A stored formula, opened for a change.
 *
 * Never null, which is the whole difference from the chain this file used to
 * hold: every `FormulaNode` IS a `DraftNode`, so there is no tree the builder
 * has to refuse to open. A formula written by the assistant, by a template or
 * by a newer version of this screen opens here and can be changed piece by
 * piece.
 */
export const draftFromSpec = (
  fieldId: string | null,
  label: string,
  spec: FormulaSpec,
  purpose: FormulaPurpose = FIELD_PURPOSE,
): FormulaDraft => ({
  editing: fieldId,
  label,
  result:
    purpose.kind === 'link' || purpose.kind === 'default'
      ? purpose.result
      : purpose.kind === 'show_when'
        ? 'boolean'
        : spec.result,
  onMissing: spec.onMissing,
  root: spec.expr,
  purpose,
});

/** The recipe already on a field, when the schema carries one. */
export const specOfField = (field: RecordSchemaField): FormulaSpec | null => {
  if (field.type !== 'formula') return null;
  const held = formulaOf(field.spec);
  if (held === null) return null;
  // Read rather than trusted: the schema is server data, and `readFormulaSpec`
  // is the one reader for a tree wherever it is stored.
  return readFormulaSpec(held);
};

/* ---------------------------------------------------- the chain, for drawing */

/** One piece of a chain: the node, and where it lives. */
export type ChainPiece = { readonly node: DraftNode; readonly path: DraftPath };

/** One operator between two pieces: what it is, and the calc that holds it. */
export type ChainOp = { readonly op: FormulaCalcOp; readonly path: DraftPath };

/**
 * The flat reading of one level.
 *
 * `pieces` is the LEFT spine, left to right, and `ops` is one shorter. The
 * right of every calc on that spine is a piece; a piece that is itself a calc
 * is a bracketed block, and the screen draws it nested rather than flat. That
 * is the whole rendering model, and it is here rather than in the screen so a
 * test can hold it.
 */
export type Chain = {
  readonly pieces: readonly ChainPiece[];
  readonly ops: readonly ChainOp[];
  /** Where "Add a piece" appends: the top of this chain. */
  readonly path: DraftPath;
};

export const chainAt = (root: DraftNode | null, path: DraftPath): Chain | null => {
  const node = nodeAt(root, path);
  if (node === null) return null;

  const pieces: ChainPiece[] = [];
  const ops: ChainOp[] = [];
  let here: DraftNode = node;
  let herePath: DraftPath = path;
  while (here.kind === 'calc') {
    pieces.push({ node: here.right, path: [...herePath, 1] });
    ops.push({ op: here.op, path: herePath });
    herePath = [...herePath, 0];
    here = here.left;
  }
  pieces.push({ node: here, path: herePath });
  return { pieces: pieces.reverse(), ops: ops.reverse(), path };
};

/* --------------------------------------------------------- what may be read */

/**
 * The fields on this record a piece may read.
 *
 * A `formula` field IS offered now, and that is Decision 4: its answer has been
 * kept in a slot since migration 0011, so reading it is reading a stored value,
 * and `formulaOrder` on both sides runs the one that is read before the one
 * that reads it. It is offered only WITH a recipe, because a formula whose tree
 * this device has not been told is one whose answered type nobody here knows.
 *
 * A `rollup` is offered when its answer type is known (`workedResultOf`): it
 * keeps its total in a slot since migration 0088, moved by the writes of the
 * entries it counts. `lookup` stays out: it has no slot, so there is nothing on
 * the row to read. A set, a file and a place stay out because none of them has
 * one value. `readableFieldType` in `@alluzo/shared` is the same table the
 * server checks against, so this screen offers exactly what will be accepted.
 */
export const readableFieldsOf = (
  schema: RecordSchemaResponse,
  exceptFieldId: string | null = null,
): readonly RecordSchemaField[] =>
  schema.fields.filter((field) => {
    if (field.id === exceptFieldId) return false;
    if (field.type === 'formula') return specOfField(field) !== null;
    if (field.type === 'rollup') return workedResultOf(field.type, field.spec) !== undefined;
    if (field.type === 'lookup') return false;
    return readableFieldType(field.type) !== null;
  });

/**
 * The `lines` fields on this row an `agg` can total over: the lines directly
 * under it, whose schema the row's own schema carries (Decision 7 of
 * docs/plans/entry-lines-and-layout.md). A field whose lines this device was
 * never told about offers nothing to total.
 */
export const linesFieldsOf = (schema: RecordSchemaResponse): readonly RecordSchemaField[] =>
  schema.fields.filter((field) => field.type === 'lines' && schema.lineSchemas?.[field.id] !== undefined);

/**
 * The fields on a line that "Total of" may add up: numbers, typed or worked
 * out. A line formula is offered too, which is how a stay adds up the Guests
 * each room counted.
 */
export const aggFieldsOf = (lines: RecordSchemaResponse): readonly RecordSchemaField[] =>
  readableFieldsOf(lines).filter((field) => {
    const reads = isComputedType(field.type) ? (workedResultOf(field.type, field.spec) ?? null) : readableFieldType(field.type);
    return reads !== null && isNumericType(reads);
  });

/**
 * Whether a hop through this field reads up the tree: it is the line's parent
 * or root reference (Decision 6 of docs/plans/entry-lines-and-layout.md). Such
 * a hop is offered only the typed fields there: a worked-out field above could
 * read this line back, which the checker refuses as FORMULA_PARENT_COMPUTED.
 */
export const readsUpTree = (schema: Pick<RecordSchemaResponse, 'bindings'>, viaFieldId: string): boolean =>
  (['parent', 'root'] as const).some((purpose) => {
    const bound = schema.bindings[purpose];
    return bound !== undefined && 'fieldId' in bound && bound.fieldId === viaFieldId;
  });

/** Every field that points at another record, so a piece can hop through it. */
export const hopFieldsOf = (schema: RecordSchemaResponse): readonly RecordSchemaField[] =>
  schema.fields.filter((field) => field.type === 'reference' && field.targetRecordId !== null);

/** The fields on the other side of a hop that a piece may read. */
export const hopTargetFieldsOf = (target: RecordSchemaResponse): readonly RecordSchemaField[] =>
  readableFieldsOf(target);

/**
 * The fields on the other side of a hop a DEFAULT may read, for a field of one
 * type.
 *
 * A one-tap answer must not offer a value the checker would refuse two screens
 * later, so this is `defaultProblems` read forwards: an amount is offered a
 * number and never a date, a field that reads a list is offered the reference
 * fields that read the SAME list, and a field of choices is offered nothing at
 * all — it starts with one of its own choices or with nothing.
 *
 * A worked-out field on the OTHER row is offered, and that is not the rule
 * about reading a worked-out field: that rule is about this row, where a
 * formula reads what a default fills. The other row was saved long before this
 * form opened.
 */
export const defaultHopFieldsOf = (
  target: RecordSchemaResponse,
  fieldType: string,
  result: FormulaResultType,
  targetRecordId: string | null,
): readonly RecordSchemaField[] => {
  if (fieldType === 'select') return [];
  return hopTargetFieldsOf(target).filter((field) => {
    if (fieldType === 'reference') {
      return (
        field.type === 'reference' &&
        field.targetRecordId !== null &&
        field.targetRecordId === targetRecordId
      );
    }
    const reads = isComputedType(field.type)
      ? (workedResultOf(field.type, field.spec) ?? null)
      : readableFieldType(field.type);
    return reads !== null && resultAccepts(result, reads);
  });
};

/* ---------------------------------------------------------------- the words */

/** What a screen needs to turn ids into words. */
export type FormulaWords = {
  readonly source: RecordSchemaResponse;
  /** The schema of every record a hop reaches, by record id. */
  readonly targets: ReadonlyMap<string, RecordSchemaResponse>;
};

const fieldOf = (schema: RecordSchemaResponse, fieldId: string): RecordSchemaField | null =>
  schema.fields.find((field) => field.id === fieldId) ?? null;

const targetOf = (words: FormulaWords, viaFieldId: string): RecordSchemaResponse | null => {
  const via = fieldOf(words.source, viaFieldId);
  if (via === null || via.type !== 'reference' || via.targetRecordId === null) return null;
  return words.targets.get(via.targetRecordId) ?? null;
};

/**
 * Where an `agg` reads its rows: the list a multi_select on the source points
 * at, or the lines record of a `lines` field, whose schema rides in the
 * source's own `lineSchemas` under the field's id.
 */
const linesTargetOf = (words: FormulaWords, linesFieldId: string): RecordSchemaResponse | null => {
  const lines = fieldOf(words.source, linesFieldId);
  if (lines === null) return null;
  if (lines.type === 'lines') return words.source.lineSchemas?.[linesFieldId] ?? null;
  if (lines.type !== 'multi_select' || lines.targetRecordId === null) return null;
  return words.targets.get(lines.targetRecordId) ?? null;
};

/**
 * The id a gap wears while the sentence is being said.
 *
 * `formulaSentence` speaks `FormulaNode`, which has no gap in it, so a gap is
 * handed over as a field whose name is the ellipsis. A sentinel and not a real
 * id: no field can be called this, because no id this app writes holds a NUL.
 */
const GAP_FIELD_ID = '\u0000gap';

/** The draft as something the shared sentence can say. Every gap reads as "…". */
const sayable = (node: DraftNode): FormulaNode => {
  if (node.kind === 'gap') return { kind: 'field', fieldId: GAP_FIELD_ID };
  const kids = childrenOf(node);
  if (kids.length === 0) return node as FormulaNode;
  return withChildren(node, kids.map(sayable)) as FormulaNode;
};

/** The lookup `formulaSentence` asks, built out of the schemas the screen holds. */
export const wordLookupOf = (words: FormulaWords): FormulaWordLookup => ({
  own: (fieldId) =>
    fieldId === GAP_FIELD_ID ? t('formula.gap') : (fieldOf(words.source, fieldId)?.label ?? null),
  hop: (via, fieldId) => {
    const viaField = fieldOf(words.source, via);
    if (viaField === null) return null;
    const target = targetOf(words, via);
    const there = target === null ? null : fieldOf(target, fieldId);
    return there === null ? null : { via: viaField.label, field: there.label };
  },
  line: (lines, fieldId) => {
    const target = linesTargetOf(words, lines);
    return target === null ? null : (fieldOf(target, fieldId)?.label ?? null);
  },
  choice: (fieldId, value) => {
    const option = fieldOf(words.source, fieldId)?.options?.find((one) => one.id === value);
    if (option === undefined) return null;
    const word = option.label ?? (option.labelKey ? tData(option.labelKey) : '');
    return word === '' ? null : word;
  },
});

/**
 * The left side of the test whose right side `path` is, or null when the
 * cursor is anywhere else. A fixed value there is compared with it, so a
 * field of choices offers its own choices, not a number to type.
 */
export const comparedLeftOf = (root: DraftNode | null, path: DraftPath): DraftNode | null => {
  if (path.length === 0 || path[path.length - 1] !== 1) return null;
  const parent = nodeAt(root, path.slice(0, -1));
  if (parent === null || parent.kind !== 'if' || parent.right === undefined) return null;
  return parent.left;
};

/** A fixed choice on the right of a test, by its own word ("Checked in"), or null for any other piece. */
export const comparandWordAt = (words: FormulaWords, root: DraftNode | null, path: DraftPath): string | null => {
  const left = comparedLeftOf(root, path);
  const here = nodeAt(root, path);
  if (left === null || left.kind !== 'field' || here === null || here.kind !== 'const' || typeof here.value !== 'string') return null;
  const word = wordLookupOf(words).choice?.(left.fieldId, here.value) ?? null;
  return word === null ? null : `"${word}"`;
};

/**
 * The lead-in on the right side of a test: "than" only where the test reads
 * with it ("is more than … "); "is … " and "is at least … " take none.
 */
export const rightKeyOf = (op: FormulaTestOp): 'than' | null => (op === 'gt' || op === 'lt' ? 'than' : null);

/** One piece, in the word a person would use. A gap says "…" and nothing else. */
export const nodeLabel = (words: FormulaWords, node: DraftNode): string =>
  formulaSentence(sayable(node), wordLookupOf(words), tData);

/** The word between two pieces: the sign, for the row that cycles it. */
export const opSign = (op: FormulaCalcOp): string => t(`formula.op.${op}`);

/** And the same operator said out loud, for the line under the sign. */
export const opWord = (op: FormulaCalcOp): string => t(`formula.op.${op}_word`);

/**
 * The whole recipe, in one line.
 *
 * Through `formulaSentence` in `@alluzo/shared`, so the builder, the link row,
 * the card caption and the assistant's proposal say one formula one way. A
 * half-built one says "…" where a piece is still missing, which is the honest
 * reading of a slot nobody has answered.
 */
export const sentenceFor = (draft: FormulaDraft, words: FormulaWords): string =>
  draft.root === null ? t('formula.nothing_yet') : nodeLabel(words, draft.root);

/* ------------------------------------------------------------- the checking */

/**
 * The lookup `checkFormula` asks, built out of the schemas the screen holds.
 *
 * `result` on a formula field is what makes it readable by another formula, and
 * `formulaOf` is what lets the cycle walk go out through one formula and come
 * back. Both are read off the schema's own `spec`, so this screen refuses
 * exactly the ring the server would.
 */
export const lookupFor = (words: FormulaWords, purpose: FormulaPurpose['kind'] = 'field'): FormulaLookup => {
  const infoOf = (field: RecordSchemaField | null) => {
    if (field === null) return null;
    const result = workedResultOf(field.type, field.spec);
    return result === undefined
      ? { id: field.id, label: field.label, type: field.type }
      : { id: field.id, label: field.label, type: field.type, result };
  };
  return {
    own: (fieldId) => infoOf(fieldOf(words.source, fieldId)),
    isReference: (fieldId) => fieldOf(words.source, fieldId)?.type === 'reference',
    hop: (viaFieldId, fieldId) => {
      const target = targetOf(words, viaFieldId);
      return target === null ? null : infoOf(fieldOf(target, fieldId));
    },
    lineField: (linesFieldId, fieldId) => {
      const target = linesTargetOf(words, linesFieldId);
      return target === null ? null : infoOf(fieldOf(target, fieldId));
    },
    /**
     * A line reads only what was typed above it (Decision 6 of the lines
     * plan): a hop through its parent or root field onto a worked-out field
     * is refused, as the server refuses it.
     */
    isParentComputed: (viaFieldId, fieldId) => {
      const bound = (['parent', 'root'] as const).some((purpose) => {
        const held = words.source.bindings[purpose];
        return held !== undefined && 'fieldId' in held && held.fieldId === viaFieldId;
      });
      if (!bound) return false;
      const target = targetOf(words, viaFieldId);
      const field = target === null ? null : fieldOf(target, fieldId);
      return field !== null && isComputedType(field.type);
    },
    formulaOf: (fieldId) => {
      const field = fieldOf(words.source, fieldId);
      return field === null ? null : specOfField(field);
    },
    /**
     * A ring through other fields, from the field graph over this record and
     * every record a hop reaches (F3 of docs/plans/field-relations.md): the
     * same rule the server holds the save to.
     */
    graphProblems: (spec, selfFieldId) =>
      formulaGraphProblems(
        [words.source, ...words.targets.values()].map((schema) => ({
          id: schema.recordId,
          fields: schema.fields.map((field) => ({
            id: field.id,
            label: field.label,
            type: field.type,
            targetRecordId: field.targetRecordId,
            spec: field.spec,
            startsWith: field.startsWith,
            startsOnce: field.startsOnce === true,
            showWhen: field.showWhen ?? null,
            // Editable when and Must be write nothing, so they close no ring;
            // the graph holds them so a rule is a dependent of what it reads.
            editableWhen: field.editableWhen ?? null,
            mustBe: (field.mustBe ?? []).map((rule) => rule.formula),
          })),
          rules: (schema.rules ?? []).map((rule) => rule.formula),
        })),
        words.source.recordId,
        // The recipe goes where it will be kept: a default's hop is read once,
        // at the write, so it closes no ring (F22), and a field keeps its type.
        purpose === 'default'
          ? { id: selfFieldId, startsWith: spec }
          : purpose === 'show_when'
            ? { id: selfFieldId, showWhen: spec }
            : { id: selfFieldId, type: 'formula', spec: { formula: spec } },
      ),
    /**
     * A select's own choice ids. Only a default asks: "one of its own choices"
     * is the whole shape rule for a select, and an id is the only thing an
     * entry ever stores.
     */
    optionsOf: (fieldId) => {
      const field = fieldOf(words.source, fieldId);
      if (field === null || field.type !== 'select') return null;
      return (field.options ?? []).map((option) => option.id);
    },
    /**
     * The list a reference field reads — on this record with no `via`, and on
     * the record `via` points at with one. Only a default asks, and "the same
     * list" is the whole of what it asks about.
     */
    targetOf: (fieldId, viaFieldId) => {
      const schema = viaFieldId === undefined ? words.source : targetOf(words, viaFieldId);
      if (schema === null) return null;
      const field = fieldOf(schema, fieldId);
      return field === null || field.type !== 'reference' ? null : field.targetRecordId;
    },
    /**
     * The choice keys of a select a `date_add` reads its unit from (F4), on
     * this record or across a hop. A schema cached before choices carried
     * keys answers nothing, so it is not refused for keys it cannot show.
     */
    optionKeysOf: (fieldId, viaFieldId) => {
      const schema = viaFieldId === undefined ? words.source : targetOf(words, viaFieldId);
      const field = schema === null ? null : fieldOf(schema, fieldId);
      if (field === null || field.type !== 'select' || field.options === null) return null;
      if (field.options.some((option) => option.key === undefined)) return null;
      return field.options.flatMap((option) => (typeof option.key === 'string' ? [option.key] : []));
    },
  };
};

/**
 * Every problem with the draft, at once. Empty means the server will take it.
 *
 * A default is checked by `defaultProblems`, which runs `checkFormula` first
 * and then the three rules a DEFAULT keeps on top of it: the type has to take
 * one at all, the recipe may not read a worked-out field, and the answer has to
 * fit the field. `options` is what the lookup cannot answer for a field that
 * does not exist yet — the choices it is being given, the list it will read.
 */
export const problemsOf = (
  draft: FormulaDraft,
  words: FormulaWords,
  options: DefaultOptions = {},
): readonly FormulaProblem[] => {
  const spec = draftToSpec(draft);
  if (spec === null) return [];
  const lookup = lookupFor(words, draft.purpose.kind);
  if (draft.purpose.kind === 'default') {
    return defaultProblems(draft.purpose.fieldType, spec, lookup, draft.editing, options);
  }
  if (draft.purpose.kind === 'show_when') {
    if (draft.purpose.rule === 'must') {
      // The tree alone: the message and the level are set beside the rule, not in the builder.
      return mustBeProblems({ formula: spec, message: '-', level: 'block' }, lookup, null);
    }
    return draft.purpose.rule === 'edit' ? editableWhenProblems(spec, lookup, draft.editing) : showWhenProblems(spec, lookup, draft.editing);
  }
  return checkFormula(spec, lookup, draft.editing);
};

export const draftIsReady = (
  draft: FormulaDraft,
  words: FormulaWords,
  options: DefaultOptions = {},
): boolean => {
  if (draftToSpec(draft) === null) return false;
  // A link mapping and a default are not fields, so neither has a name of its
  // own to give: one belongs to the field it lands in, the other to the field
  // it starts.
  if (draft.purpose.kind === 'field' && draft.label.trim() === '') return false;
  return problemsOf(draft, words, options).length === 0;
};

/**
 * Which fields this recipe FOLLOWS, in the person's own words.
 *
 * Worked out from the tree and never chosen, which is why the builder draws it
 * as a fact: the recipe reads the Customer field, so whenever the customer
 * changes and nobody has typed into the field, it fills again. A field the
 * record no longer has falls back to its id, the same as the sentence does.
 */
export const followedFields = (draft: FormulaDraft, words: FormulaWords): readonly string[] => {
  const spec = draftToSpec(draft);
  if (spec === null) return [];
  return formulaFieldsRead(spec).map((fieldId) => fieldOf(words.source, fieldId)?.label ?? fieldId);
};

/* ------------------------------------------------------------- the preview */

/**
 * What the formula would answer on one real row.
 *
 * A builder with no preview asks a person to trust a rule they cannot see run.
 * The screen passes the newest entry it holds and the values behind its hops,
 * and gets back the same number the server will work out.
 */
/**
 * The lines reader the preview hands an `agg`: the sample row's own lines,
 * read off the mirror. Null when the recipe totals a multi select: those rows
 * are rows of a list, chosen by id, and the lines of the sample row would
 * answer a different question.
 */
export const previewLinesReader = (
  draft: FormulaDraft,
  source: Pick<RecordSchemaResponse, 'fields'>,
  lines: EntryLinesWrite,
): LinesReader | null => {
  const spec = draftToSpec(draft);
  if (spec === null) return null;
  const read = formulaLinesRead(spec);
  const allLines = read.every(({ lines: id }) => source.fields.find((field) => field.id === id)?.type === 'lines');
  if (!allLines) return null;
  return (fieldId) => (lines[fieldId] ?? []).map((line) => line.values);
};

export const previewOf = (
  draft: FormulaDraft,
  values: FormulaValues,
  readHop: ((entryId: string, fieldId: string) => unknown) | null,
  readLines: LinesReader | null = null,
  /** A select's choice key by its id, for a `date_add` whose unit is a select (F4). */
  optionKey: OptionKeyReader | null = null,
): string | number | boolean | null => {
  const spec = draftToSpec(draft);
  if (spec === null) return null;
  // A total over many rows needs those rows. With no reader for them here,
  // `onMissing: 'zero'` would answer the rest of the sum alone, which is a
  // confident wrong figure, so the preview says nothing instead.
  if (formulaLinesRead(spec).length > 0 && readLines === null) return null;
  // A rule that reads `today` (a Starts with, a Show when) previews with this device's day (F8).
  return evaluateFormula(spec, values, readHop, readLines, { today: localDayOf(new Date()), optionKey });
};

/**
 * The rule a "Show when" opens on: "If … is … then yes, otherwise no". The
 * question is the one thing a person has to answer, so the builder opens on a
 * tree where that is the only gap left, and the yes and the no are already
 * there (Step E1 of docs/plans/entry-lines-and-layout.md).
 */
export const showWhenSeed = (fieldKey: string, label: string, rule: 'show' | 'edit' | 'must' = 'show', at?: number): FormulaDraft => ({
  ...blankDraft({ kind: 'show_when', fieldKey, ...(rule === 'show' ? {} : { rule }), ...(at === undefined ? {} : { at }) }),
  label,
  editing: fieldKey === RECORD_RULES_KEY ? null : fieldKey,
  root: {
    kind: 'if',
    left: GAP,
    op: 'eq',
    right: GAP,
    then: { kind: 'const', value: true },
    otherwise: { kind: 'const', value: false },
  },
});

/** The five kinds of answer a formula may be, in the order a shop needs them. A date since F9. */
export const RESULT_KINDS: readonly FormulaResultType[] = ['money', 'number', 'quantity', 'text', 'date'];

/* ------------------------------------------------------------- the proposal */

/** One thing the nudge's chain multiplies. The three sources it may name. */
export type FormulaTerm =
  | { readonly kind: 'field'; readonly fieldId: string }
  | { readonly kind: 'hop'; readonly via: string; readonly fieldId: string }
  | { readonly kind: 'const'; readonly value: number };

/**
 * The draft the nudge hands the builder.
 *
 * `editing` stays null: this is a new field the card proposed, not an existing
 * one being changed. The person lands on the builder with the recipe already
 * built and every row still theirs to change, which is what makes the card an
 * offer rather than a switch.
 *
 * The proposal is still a flat chain, because the only thing the nudge ever
 * proposes is one multiplication. It folds left into the tree here, which is
 * the same fold the builder's own "Add a piece" makes.
 */
export const draftFromProposal = (proposal: {
  readonly label: string;
  readonly terms: readonly FormulaTerm[];
  readonly ops: readonly FormulaCalcOp[];
  readonly result: FormulaResultType;
}): FormulaDraft => {
  let root: DraftNode | null = null;
  proposal.terms.forEach((term, index) => {
    const node: DraftNode = term;
    if (root === null) {
      root = node;
      return;
    }
    root = { kind: 'calc', op: proposal.ops[index - 1] ?? 'multiply', left: root, right: node };
  });
  return {
    editing: null,
    label: proposal.label,
    result: proposal.result,
    onMissing: 'blank',
    root,
    purpose: FIELD_PURPOSE,
  };
};
